// The revoke-a-PAT write flow (#835) — the state half of the tokens page's
// confirm step, split from the route the same way `createMachineRemove` is: a
// mutation with a confirm, a re-entrancy guard and a failure notice is state,
// not rendering. Per-surface, never a global store.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { type ApiToken, revokeApiToken } from './tokens';

export type TokenRevokeController = {
  /** The token awaiting confirmation, or null. Feed to the <ConfirmDialog/>. */
  readonly target: () => ApiToken | null;
  /** Failure copy for the surface's <Toast/>, or null. */
  readonly error: () => string | null;
  /** Open the confirm for a token, clearing any previous failure. */
  readonly ask: (token: ApiToken) => void;
  /** Dismiss the confirm without revoking. */
  readonly close: () => void;
  /** Clear the failure notice — wire to the <Toast/>'s `onDismiss`. */
  readonly clearError: () => void;
  /** Revoke the token under confirmation. A second call mid-flight is dropped. */
  readonly confirm: () => void;
};

/**
 * Revoke state for one surface. `onRevoked` fires once the API has accepted the
 * revoke — the caller re-reads its roster and says so. A failure leaves the
 * dialog open with `error()` set: killing a credential must never silently
 * no-op, or a maintainer walks away believing a leaked token is dead.
 */
export function createTokenRevoke(input: {
  readonly token: string | null;
  readonly onRevoked: () => void;
}): TokenRevokeController {
  const [target, setTarget] = createSignal<ApiToken | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  // Busy guard instead of a disabled action button (ConfirmDialog has none).
  let busy = false;

  const confirm = (): void => {
    const row = target();
    if (row === null || busy) return;
    busy = true;
    setError(null);
    void revokeApiToken(row.id, input.token)
      .then(() => {
        // Close only the confirm THIS call opened: nothing stops the reader
        // dismissing it and asking about a different token while the revoke is
        // in flight, and a blind clear would dismiss that newer dialog out from
        // under them — reading as a phantom cancel of a token they never killed.
        if (target()?.id === row.id) setTarget(null);
        input.onRevoked();
      })
      .catch(() => setError(t('account_tokens.revoke_error')))
      .finally(() => {
        busy = false;
      });
  };

  return {
    target,
    error,
    ask: (token) => {
      setError(null);
      setTarget(token);
    },
    close: () => setTarget(null),
    clearError: () => setError(null),
    confirm,
  };
}
